import { toEventId } from '@atlassian/jira-forge-ui-types/src/common/types/analytics.tsx';
import type { CustomUIBridge } from '@atlassian/jira-forge-ui-types/src/common/types/bridge.tsx';
import type { CustomBridgeMethods } from '@atlassian/jira-forge-ui-types/src/common/types/custom-bridge-methods.tsx';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import { fireOperationalEvent, fireTrackEvent } from '../../common/utils/consumers/index.tsx';

export type BridgeMethodName = keyof (CustomUIBridge & CustomBridgeMethods);

export type Fn = (methodName: BridgeMethodName, attributes?: Attributes) => Promise<void>;

type Reporter = {
	fireTrackMethodCalledEvent: Fn;
	fireOperationalMethodFailedSyncEvent: Fn;
	fireOperationalMethodFailedAsyncEvent: Fn;
};

export type BridgeReporter = Reporter;
export type CustomBridgeMethodsReporter = Reporter;

// ---------------
// Bridge reporter
// ---------------

const bridgeEventId = toEventId('bridge');

const fireTrackBridgeMethodCalledEvent = (methodName: BridgeMethodName, attributes?: Attributes) =>
	fireTrackEvent(`${methodName} called`, {
		id: bridgeEventId,
		attributes,
	});

const fireOperationalBridgeMethodFailedSyncEvent = (
	methodName: BridgeMethodName,
	attributes?: Attributes,
) =>
	fireOperationalEvent(`${methodName} failed sync`, {
		id: bridgeEventId,
		attributes,
	});

const fireOperationalBridgeMethodFailedAsyncEvent = (
	methodName: BridgeMethodName,
	attributes?: Attributes,
) =>
	fireOperationalEvent(`${methodName} failed async`, {
		id: bridgeEventId,
		attributes,
	});

// ------------------------------
// Custom bridge method reporters
// ------------------------------

const customBridgeMethodEventId = toEventId('customBridgeMethod');

const fireTrackCustomBridgeMethodCalledEvent = (
	methodName: BridgeMethodName,
	attributes?: Attributes,
) =>
	fireTrackEvent(`${methodName} called`, {
		id: customBridgeMethodEventId,
		attributes,
	});

const fireOperationalCustomBridgeMethodFailedSyncEvent = (
	methodName: BridgeMethodName,
	attributes?: Attributes,
) =>
	fireOperationalEvent(`${methodName} failed sync`, {
		id: customBridgeMethodEventId,
		attributes,
	});

const fireOperationalCustomBridgeMethodFailedAsyncEvent = (
	methodName: BridgeMethodName,
	attributes?: Attributes,
) =>
	fireOperationalEvent(`${methodName} failed async`, {
		id: customBridgeMethodEventId,
		attributes,
	});

// -------
// Exports
// -------

export const bridgeReporter: BridgeReporter = {
	fireTrackMethodCalledEvent: fireTrackBridgeMethodCalledEvent,
	fireOperationalMethodFailedSyncEvent: fireOperationalBridgeMethodFailedSyncEvent,
	fireOperationalMethodFailedAsyncEvent: fireOperationalBridgeMethodFailedAsyncEvent,
};

export const customBridgeMethodsReporter: CustomBridgeMethodsReporter = {
	fireTrackMethodCalledEvent: fireTrackCustomBridgeMethodCalledEvent,
	fireOperationalMethodFailedSyncEvent: fireOperationalCustomBridgeMethodFailedSyncEvent,
	fireOperationalMethodFailedAsyncEvent: fireOperationalCustomBridgeMethodFailedAsyncEvent,
};
